<template>
  <div
    ref="root"
    class="rd rd--team"
    :class="{ 'ui-compact': phone }"
    :data-w="layout"
    :data-density="density"
    :data-bar="barSticky ? 'sticky' : 'flow'"
    :style="rootStyle"
  >
    <div class="rd-main">
      <div class="rd-page">
        <div class="rd-head">
          <div class="rd-head__title">
            <h1>{{ $t("delivery_team.title") }}</h1>
            <p class="rd-head__sub">{{ subtitle }}</p>
          </div>
          <div class="rd-head__right">
            <button
              type="button"
              class="ui-icon-btn ui-icon-btn--sm"
              :title="`${$t('refresh')} (r)`"
              :aria-label="$t('refresh')"
              @click="reload"
            >
              <v-icon size="18">mdi-refresh</v-icon>
            </button>
            <button
              type="button"
              class="ui-icon-btn ui-icon-btn--sm ui-icon-btn--plain"
              :title="`${$t('report_desktop.shortcuts')} (?)`"
              :aria-label="$t('report_desktop.shortcuts')"
              :aria-expanded="String(helpOpen)"
              @click="helpOpen = !helpOpen"
            >
              <v-icon size="18">mdi-keyboard-outline</v-icon>
            </button>
            <button
              type="button"
              class="ui-icon-btn ui-icon-btn--sm"
              :title="$t('print')"
              :aria-label="$t('print')"
              @click="printPage"
            >
              <v-icon size="18">mdi-printer-outline</v-icon>
            </button>
            <button
              v-if="isAdmin"
              type="button"
              class="ui-icon-btn ui-icon-btn--sm"
              :title="$t('delivery_team.more.search_id')"
              :aria-label="$t('delivery_team.more.search_id')"
              @click="searchById = true"
            >
              <v-icon size="18">mdi-account-search-outline</v-icon>
            </button>
            <div class="dt-split">
              <ui-button
                variant="primary"
                icon="mdi-plus"
                class="dt-split__main"
                aria-keyshortcuts="n"
                @click="openForm(0)"
                >{{ phone ? $t("delivery_team.new_short") : $t("new_user") }}</ui-button
              >
              <v-menu v-model="moreOpen" offset-y bottom left nudge-bottom="6">
                <template #activator="{ on, attrs }">
                  <button
                    type="button"
                    class="ui-btn ui-btn--primary dt-split__more"
                    :aria-label="$t('delivery_team.more.label')"
                    aria-haspopup="menu"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon size="18" color="currentColor">mdi-menu-down</v-icon>
                  </button>
                </template>
                <div class="rd dt-more" role="menu">
                  <button type="button" role="menuitem" @click="runMore('quickAdd')">
                    {{ $t("add_multiple_users") }}
                    <small>{{ $t("delivery_team.more.add_many_hint") }}</small>
                  </button>
                </div>
              </v-menu>
            </div>
            <button
              type="button"
              class="ui-text-btn rd-nav__legacy"
              :title="$t('report_desktop.old_view')"
              @click="$emit('legacy')"
            >
              <v-icon size="16" color="#c2410c">mdi-history</v-icon>
              <span class="rd-nav__legacy-label">{{
                $t("report_desktop.old_view")
              }}</span>
            </button>
          </div>
        </div>
      </div>

      <ui-filter-bar
        v-if="status === 'ready' && users.length"
        ref="bar"
        :segments="segments"
        :query="query"
        :placeholder="$t('delivery_team.search_placeholder')"
        :search-label="$t('search')"
        :count="$t('delivery_team.count', { shown: visible.length, total: users.length })"
        @segment="onSegment"
        @query="query = $event"
      />

      <div class="rd-page ui-screen">
        <ui-state v-if="status === 'loading'" kind="loading" />
        <ui-state
          v-else-if="status === 'error'"
          kind="error"
          icon="mdi-alert-outline"
          :title="$t('delivery_team.state.error_title')"
          :text="$t('delivery_team.state.error_text')"
        >
          <ui-button variant="dark" icon="mdi-refresh" @click="reload">{{
            $t("delivery_team.state.retry")
          }}</ui-button>
        </ui-state>
        <ui-state
          v-else-if="!users.length"
          kind="empty"
          icon="mdi-account-group-outline"
          :title="$t('delivery_team.state.empty_title')"
          :text="$t('delivery_team.state.empty_text')"
        >
          <ui-button variant="primary" icon="mdi-plus" @click="openForm(0)">{{
            $t("new_user")
          }}</ui-button>
        </ui-state>
        <template v-else>
          <ui-kpis
            :tiles="kpiTiles"
            :label="$t('delivery_team.kpi.label')"
            @pick="pickKpi"
          />
          <ui-state
            v-if="!visible.length"
            kind="empty"
            icon="mdi-magnify"
            :title="$t('delivery_team.state.filtered_title')"
            :text="$t('delivery_team.state.filtered_text')"
          >
            <ui-button @click="resetFilters">{{
              $t("delivery_team.state.reset")
            }}</ui-button>
          </ui-state>
          <team-list
            v-else
            :users="visible"
            :active-id="activeId"
            :currency="currency"
            @open="(user) => openUser(user.id)"
          />
        </template>
      </div>
    </div>

    <report-detail-panel
      :stack="panelStack"
      @close="closePanel"
      @back="panelBack"
      @open="openPanel"
      @toast="onPanelToast"
    />
    <transition name="rd-fade">
      <div v-if="panelStack.length" class="rd-scrim" @click="closePanel"></div>
    </transition>

    <report-help-popover
      :open="helpOpen"
      :shortcuts="shortcuts"
      @close="helpOpen = false"
    />
    <ui-toast :toast="toast" @action="runToastAction" />

    <company-employee-qr-dialog v-model="qrDialog" :employee="qrUser" />
    <company-employee-quick-add-dialog
      v-model="quickAdd"
      :restaurant-name="companyName"
      @employeeAdded="onQuickAdded"
    />
    <company-employee-search-dialog v-model="searchById" />
  </div>
</template>

<script>
import "@/components/report/desktop/reportDesktop.scss";
import "./teamShell.scss";
import ReportHelpPopover from "@/components/report/shared/ReportHelpPopover.vue";
import ReportDetailPanel from "@/components/report/desktop/ReportDetailPanel.vue";
import hasReportShell from "@/components/report/shared/hasReportShell";
import CompanyEmployeeQrDialog from "@/components/company/employee/dialogs/CompanyEmployeeQrDialog.vue";
import CompanyEmployeeQuickAddDialog from "@/components/company/employee/dialogs/CompanyEmployeeQuickAddDialog.vue";
import CompanyEmployeeSearchDialog from "@/components/company/employee/dialogs/CompanyEmployeeSearchDialog.vue";
import employeeService from "@/services/employee";
import { getDeliveryCompanyCurrency } from "@/utils/currency";
import hasTeamLabels from "./hasTeamLabels";
import TeamList from "./parts/TeamList.vue";
import MemberPanel from "./panels/MemberPanel.vue";
import MemberFormPanel from "./panels/MemberFormPanel.vue";
import { TEAM_QUERY } from "./teamQuery";
import {
  buildBody,
  countUsers,
  filterUsers,
  mapServerErrors,
  mapUser,
  unwrapCompany,
} from "./teamLogic";

/**
 * Korisnici dostavne firme (/firma-dostava/:id/korisnici): kuriri i dispečeri. Isti izgled kao izvještaji, magacin, recepti i
 * finansije: zaglavlje, brojke koje filtriraju, traka filtera, lista (tabela ili kartice) i detalji, unos i izmjena u desnom
 * panelu. Vrsta i status filtera su u adresi (?k=&s=); ?ui=stari vraća staru stranicu.
 * Panele čitaju korisnike iz ove ljuske (inject reportShell), pa se kartica sama osvježi kad se lista ponovo učita.
 */
export default {
  name: "DeliveryTeamShell",
  mixins: [hasReportShell, hasTeamLabels],
  components: {
    ReportHelpPopover,
    ReportDetailPanel,
    CompanyEmployeeQrDialog,
    CompanyEmployeeQuickAddDialog,
    CompanyEmployeeSearchDialog,
    TeamList,
  },
  data: () => ({
    status: "loading",
    users: [],
    company: null,
    query: "",
    moreOpen: false,
    qrDialog: false,
    qrUser: null,
    quickAdd: false,
    searchById: false,
  }),
  computed: {
    phone() {
      return Boolean(this.device && this.device.isMobile);
    },
    companyId() {
      return (
        this.$route.params.id ||
        (this.$store.state.company && this.$store.state.company.id)
      );
    },
    companyName() {
      return this.toLatin((this.company && this.company.name) || "");
    },
    subtitle() {
      return `${this.companyName} · #${this.companyId}`;
    },
    currency() {
      return getDeliveryCompanyCurrency(this.company) || "KM";
    },
    isAdmin() {
      return Boolean(
        this.$store.state.user &&
          this.$store.state.user.user &&
          this.$store.state.user.user.type === "admin"
      );
    },
    kind() {
      const wanted = this.$route.query[TEAM_QUERY.kind];
      return ["dostava", "admin_delivery"].includes(wanted) ? wanted : "all";
    },
    filterStatus() {
      const wanted = this.$route.query[TEAM_QUERY.status];
      return ["active", "inactive", "suspended", "incomplete"].includes(wanted)
        ? wanted
        : "all";
    },
    counts() {
      return countUsers(this.users);
    },
    /** segmenti u traci filtera: vrsta (svi, kuriri, dispečeri) i status */
    segments() {
      return [
        {
          id: "kind",
          label: this.$t("role"),
          value: this.kind,
          options: [
            { value: "all", label: this.$t("all") },
            { value: "dostava", label: this.$t("delivery_team.kpi.couriers") },
            { value: "admin_delivery", label: this.$t("delivery_team.dispatchers") },
          ],
        },
        {
          id: "status",
          label: this.$t("status"),
          value: this.filterStatus,
          options: [
            { value: "all", label: this.$t("delivery_team.status.all") },
            { value: "active", label: this.$t("delivery_team.status.actives") },
            { value: "inactive", label: this.$t("delivery_team.status.inactives") },
            { value: "suspended", label: this.$t("delivery_team.kpi.suspended") },
          ],
        },
      ];
    },
    /** brojke na vrhu: ukupno, kuriri, suspendovani i nepotpun profil; svaka je i filter */
    kpiTiles() {
      const { counts, kind, filterStatus: status } = this;
      return [
        {
          key: "all",
          label: this.$t("delivery_team.kpi.all"),
          value: counts.all,
          on: kind === "all" && status === "all",
        },
        {
          key: "couriers",
          label: this.$t("delivery_team.kpi.couriers"),
          value: counts.couriers,
          on: kind === "dostava" && status === "all",
        },
        {
          key: "suspended",
          label: this.$t("delivery_team.kpi.suspended"),
          value: counts.suspended,
          on: status === "suspended",
          warn: counts.suspended > 0,
        },
        {
          key: "incomplete",
          label: this.$t("delivery_team.kpi.incomplete"),
          value: counts.incomplete,
          on: status === "incomplete",
          warn: counts.incomplete > 0,
        },
      ];
    },
    visible() {
      return filterUsers(
        this.users,
        { kind: this.kind, status: this.filterStatus, query: this.query },
        this.toLatin
      );
    },
    activeId() {
      const top = this.panelStack[this.panelStack.length - 1];
      return top && top.props ? top.props.userId || 0 : 0;
    },
    menuKeys() {
      return [];
    },
    shortcuts() {
      return [
        { keys: "n", label: this.$t("delivery_team.keys.new") },
        { keys: "/", label: this.$t("delivery_team.keys.search") },
        { keys: "r", label: this.$t("delivery_team.keys.refresh") },
        { keys: "Esc", label: this.$t("report_desktop.keys_close") },
        { keys: "?", label: this.$t("report_desktop.shortcuts") },
      ];
    },
  },
  watch: {
    companyId() {
      this.closePanel();
      this.loadUsers();
    },
  },
  created() {
    this.loadUsers();
  },
  methods: {
    // hasReportShell zove ove kad korisnik pritisne r ili [ ]
    reload() {
      this.loadUsers({ silent: this.status === "ready" });
    },
    shiftDay() {
      // lista nema dane
    },
    openScreen() {
      // jedan ekran, bez menija
    },
    // ------------------------------------------------------------ podaci
    async loadUsers({ silent = false } = {}) {
      if (!silent) this.status = "loading";
      try {
        const response = await employeeService.getCompanyUsersDelivery(this.companyId);
        const body = unwrapCompany(response.data);
        this.company = body;
        this.users = (body.users || []).map(mapUser);
        this.status = "ready";
      } catch (error) {
        if (!silent) {
          this.status = "error";
        } else {
          this.showToast(this.$t("error_loading_employees"));
        }
      }
    },
    // ------------------------------------------------------------ filteri
    setQuery(patch) {
      const query = { ...this.$route.query, ...patch };
      Object.keys(query).forEach(
        (key) => (query[key] === undefined || query[key] === "all") && delete query[key]
      );
      this.$router.replace({ query }).catch(() => {});
    },
    onSegment(id, value) {
      if (id === "kind") this.setKind(value);
      else this.setStatus(value);
    },
    setKind(kind) {
      this.setQuery({ [TEAM_QUERY.kind]: kind === "all" ? undefined : kind });
    },
    setStatus(status) {
      this.setQuery({ [TEAM_QUERY.status]: status === "all" ? undefined : status });
    },
    pickKpi(key) {
      this.query = "";
      if (key === "all")
        this.setQuery({ [TEAM_QUERY.kind]: undefined, [TEAM_QUERY.status]: undefined });
      else if (key === "couriers")
        this.setQuery({ [TEAM_QUERY.kind]: "dostava", [TEAM_QUERY.status]: undefined });
      else this.setQuery({ [TEAM_QUERY.kind]: undefined, [TEAM_QUERY.status]: key });
    },
    resetFilters() {
      this.query = "";
      this.setQuery({ [TEAM_QUERY.kind]: undefined, [TEAM_QUERY.status]: undefined });
    },
    // ------------------------------------------------------------ paneli
    openUser(id) {
      const user = this.users.find((item) => item.id === id);
      if (!user) return;
      this.openPanel({
        key: `member-${id}`,
        title: this.memberName(user),
        subtitle: `#${id}`,
        component: MemberPanel,
        props: { userId: id },
        root: true,
      });
    },
    openForm(id = 0) {
      const user = id ? this.users.find((item) => item.id === id) : null;
      if (id && !user) return;
      this.openPanel({
        key: `member-form-${id}`,
        title: id ? this.$t("edit_user") : this.$t("new_user"),
        subtitle: user
          ? this.memberName(user)
          : this.$t("delivery_team.form.required_note"),
        component: MemberFormPanel,
        props: { userId: id },
        root: true,
      });
    },
    cancelForm(id) {
      if (id) this.openUser(id);
      else this.closePanel();
    },
    onPanelToast(text, options) {
      this.showToast(text, options || {});
    },
    openQr(user) {
      this.qrUser = user.raw;
      this.qrDialog = true;
    },
    runMore(action) {
      this.moreOpen = false;
      if (action === "quickAdd") this.quickAdd = true;
    },
    printPage() {
      window.print();
    },
    onQuickAdded() {
      this.loadUsers({ silent: true });
    },
    // ------------------------------------------------------------ čuvanje (zove ga forma u panelu)
    /** Vraća { fields, rest } kad backend odbije podatke (forma ih prikazuje uz polja), a prazno kad je sve uspjelo. */
    async submitMember(form, original) {
      const isNew = !form.id;
      const body = buildBody(form, { isNew, isAdmin: this.isAdmin, original });
      try {
        let id = form.id;
        if (isNew) {
          const response = await employeeService.createCompanyUser(
            this.companyId,
            body,
            true
          );
          const created = response && response.data && response.data.data;
          id = created && created.id ? created.id : 0;
        } else {
          await employeeService.updateUserDelivery(this.companyId, form.id, body);
        }
        await this.loadUsers({ silent: true });
        this.showToast(
          this.$t(isNew ? "user_created_successfully" : "user_updated_successfully")
        );
        if (id && this.users.some((item) => item.id === id)) this.openUser(id);
        else this.closePanel();
        return {};
      } catch (error) {
        const response = error.response;
        if (response && response.status === 422) {
          const mapped = mapServerErrors(response);
          // odgovor sa status "failed" nosi ključ prevoda (npr. email već postoji)
          if (
            response.data &&
            response.data.status === "failed" &&
            response.data.message &&
            !Object.keys(mapped.fields).length
          ) {
            mapped.fields = {
              email: this.$te(response.data.message)
                ? this.$t(response.data.message)
                : response.data.message,
            };
          }
          return mapped;
        }
        const message = response && response.data && response.data.message;
        if (response && response.data && response.data.status === "failed" && message) {
          return {
            fields: { email: this.$te(message) ? this.$t(message) : message },
            rest: "",
          };
        }
        return { fields: {}, rest: this.$t("delivery_team.form.save_failed") };
      }
    },
    /** Deaktivacija: backend postavlja pivot.active = 0 (red ostaje u listi kao neaktivan). */
    async deactivateMember(user) {
      try {
        await employeeService.deleteUserDelivery(this.companyId, user.id);
        await this.loadUsers({ silent: true });
        this.showToast(this.$t("delivery_team.panel.deactivated"));
      } catch (error) {
        const data = error.response && error.response.data;
        if (data && data.error_code === "already_removed") {
          await this.loadUsers({ silent: true });
          this.showToast(this.$t("already_removed"));
        } else {
          this.showToast(
            data && data.message
              ? this.toLatin(data.message)
              : this.$t("employee_delete_failed")
          );
        }
      }
    },
    onKeydown(event) {
      const target = event.target;
      const typing =
        target &&
        (["INPUT", "SELECT", "TEXTAREA"].includes(target.tagName) ||
          target.isContentEditable);
      if (
        event.key === "n" &&
        !typing &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !document.querySelector(".v-dialog--active")
      ) {
        event.preventDefault();
        this.openForm(0);
        return;
      }
      hasReportShell.methods.onKeydown.call(this, event);
    },
  },
};
</script>
